import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { toast } from "react-toastify";
import {
    prepareMarketplaceListing,
    publishMarketplaceListing,
    getInventoryListings,
} from "../../../store/actions/marketplaceListing.action";

const EBAY_MARKETPLACE = "ebay";
const POLICY_HUB_URL = "https://www.bizpolicy.ebay.com/businesspolicy/manage";
const LOCATION_HUB_URL = "https://www.ebay.com/sh/lst/active";

/**
 * Render a single dynamic aspect field. eBay aspects come back with
 *   { name, type, mode, cardinality, required, values, suggestedValues }
 * and we translate that to the right input.
 */
function AspectField({ aspect, value, onChange }) {
    const isEnum = aspect.mode === "SELECTION_ONLY" && aspect.values?.length > 0;
    const isMulti = aspect.cardinality === "MULTI";
    const isNumber = aspect.type === "NUMBER";

    if (isEnum && !isMulti) {
        return (
            <Form.Select value={value || ""} onChange={(e) => onChange(e.target.value)}>
                <option value="">{`-- Select ${aspect.name} --`}</option>
                {aspect.values.map((v) => (
                    <option key={v} value={v}>{v}</option>
                ))}
            </Form.Select>
        );
    }

    if (isMulti) {
        const suggestedHint = aspect.suggestedValues?.length
            ? ` (e.g., ${aspect.suggestedValues.slice(0, 3).join(", ")})`
            : "";
        const placeholder = `Enter values separated by commas${suggestedHint}`;
        return (
            <Form.Control
                type="text"
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    return (
        <Form.Control
            type={isNumber ? "number" : "text"}
            placeholder={`Enter ${aspect.name}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

AspectField.propTypes = {
    aspect: PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
        mode: PropTypes.string,
        cardinality: PropTypes.string,
        required: PropTypes.bool,
        values: PropTypes.arrayOf(PropTypes.string),
        suggestedValues: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
AspectField.defaultProps = { value: "" };

/**
 * Pick the right submit-button label for the four (submitting × existingListing) states.
 */
function publishButtonLabel(submitting, isUpdate) {
    if (submitting && isUpdate) return "Updating…";
    if (submitting) return "Publishing…";
    if (isUpdate) return "Update Listing";
    return "Publish to eBay";
}

/**
 * Convert form values back to eBay's expected `{ aspectName: [values] }` shape.
 * eBay rejects bare strings — every value is wrapped in an array even for SINGLE cardinality.
 */
function buildAspectsPayload(aspectFormState, allAspects) {
    const out = {};
    for (const aspect of allAspects) {
        const raw = aspectFormState[aspect.name];
        if (raw === undefined || raw === null || raw === "") continue;
        if (aspect.cardinality === "MULTI") {
            const arr = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
            if (arr.length) out[aspect.name] = arr;
        } else {
            out[aspect.name] = [String(raw)];
        }
    }
    return out;
}

/**
 * eBay step in the MarketplacePushWizard.
 *
 * Self-contained: loads its own prereqs on mount, manages its own form state,
 * publishes when the user clicks Publish. Reports back to the wizard via
 * onPublishComplete(result) so the wizard can mark the step status and decide
 * whether to auto-advance.
 */
export default function EbayStep({ product, marketplaceId, onPublishComplete, onSkip }) {
    const [loading, setLoading] = useState(false);
    const [prereqs, setPrereqs] = useState(null);
    const [missingPolicies, setMissingPolicies] = useState(null);
    const [policiesOptedOut, setPoliciesOptedOut] = useState(null);
    const [missingLocations, setMissingLocations] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [existingListing, setExistingListing] = useState(null);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [quantity, setQuantity] = useState("");
    const [condition, setCondition] = useState("New");
    const [categoryId, setCategoryId] = useState("");
    const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState("");
    const [paymentPolicyId, setPaymentPolicyId] = useState("");
    const [returnPolicyId, setReturnPolicyId] = useState("");
    const [merchantLocationKey, setMerchantLocationKey] = useState("");
    const [aspectValues, setAspectValues] = useState({});
    const [showRecommended, setShowRecommended] = useState(false);
    const [showOptional, setShowOptional] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [publishResult, setPublishResult] = useState(null);

    const productId = product?.id || product?.Id;

    useEffect(() => {
        if (!productId) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setLoadError(null);
            setMissingPolicies(null);
            setPoliciesOptedOut(null);
            setMissingLocations(null);
            setPrereqs(null);
            setPublishResult(null);
            try {
                const [prepResp, existingResp] = await Promise.all([
                    prepareMarketplaceListing(EBAY_MARKETPLACE, {
                        companySellerId: product.companySellerId,
                        inventoryProductId: productId,
                        marketplaceRegion: "EBAY_US",
                    }),
                    getInventoryListings({ inventoryProductId: productId }).catch(() => ({ data: { EntityList: [] } })),
                ]);
                if (cancelled) return;

                const body = prepResp?.data;
                if (body?.success === false && body?.error?.code === "EBAY_BUSINESS_POLICIES_OPTED_OUT") {
                    setPoliciesOptedOut(body.error.message || "Seller has opted out of eBay business policies.");
                    return;
                }
                if (body?.success === false && body?.error?.code === "MISSING_POLICIES") {
                    setMissingPolicies(body.error.message || "Missing eBay business policies.");
                    return;
                }
                if (body?.success === false && body?.error?.code === "MISSING_LOCATIONS") {
                    setMissingLocations(body.error.message || "No eBay inventory locations.");
                    return;
                }
                if (body?.success !== true) {
                    setLoadError(body?.error?.message || "Failed to load eBay listing fields.");
                    return;
                }

                const data = body.data || {};
                setPrereqs(data);

                const p = data.product || {};
                setTitle(p.productName || product.productName || "");
                setDescription(p.productDescription || product.productDescription || "");
                setPrice(p.sellingPrice ? String(p.sellingPrice) : "");
                setQuantity(p.quantity ? String(p.quantity) : "1");
                setCondition(p.condition || p.productCondition || "New");
                setCategoryId(data.suggestedCategory?.categoryId || "");
                setFulfillmentPolicyId(data.policies?.fulfillment?.[0]?.id || "");
                setPaymentPolicyId(data.policies?.payment?.[0]?.id || "");
                setReturnPolicyId(data.policies?.return?.[0]?.id || "");
                setMerchantLocationKey(data.locations?.[0]?.key || "");

                const listings = existingResp?.data?.EntityList || [];
                const existingEbay = listings.find((l) => String(l.marketplaceName).toLowerCase() === "ebay");
                if (existingEbay) setExistingListing(existingEbay);
            } catch (err) {
                if (!cancelled) setLoadError(err?.response?.data?.error?.message || err.message || "Failed to load eBay listing fields.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [productId]);

    const allAspects = useMemo(() => {
        if (!prereqs) return [];
        return [
            ...(prereqs.requiredFields || []),
            ...(prereqs.recommendedFields || []),
            ...(prereqs.optionalFields || []),
        ];
    }, [prereqs]);

    const handleAspectChange = (name, value) => {
        setAspectValues((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!title.trim()) { toast.error("Title is required."); return false; }
        if (!price || Number.parseFloat(price) <= 0) { toast.error("Enter a valid price."); return false; }
        if (!quantity || Number.parseInt(quantity, 10) <= 0) { toast.error("Enter a valid quantity."); return false; }
        if (!categoryId) { toast.error("Select a category."); return false; }
        if (!fulfillmentPolicyId) { toast.error("Select a fulfillment policy."); return false; }
        if (!paymentPolicyId) { toast.error("Select a payment policy."); return false; }
        if (!returnPolicyId) { toast.error("Select a return policy."); return false; }
        if (!merchantLocationKey) { toast.error("Select a warehouse location."); return false; }
        for (const a of (prereqs?.requiredFields || [])) {
            const v = aspectValues[a.name];
            if (v === undefined || v === null || String(v).trim() === "") {
                toast.error(`${a.name} is required.`);
                return false;
            }
        }
        return true;
    };

    const handlePublish = async () => {
        if (!validate()) return;

        setSubmitting(true);
        try {
            const p = prereqs.product || {};
            const sku = p.eBaySku || p.productSku || product.productSku;
            const imageUrls = (p.images || [])
                .filter((img) => !img.isDeleted)
                .map((img) => img.imageUrl)
                .filter(Boolean);
            if (!imageUrls.length && (product.imageUrl || p.imageUrl)) {
                imageUrls.push(product.imageUrl || p.imageUrl);
            }

            const payload = {
                sku,
                title: title.trim(),
                description,
                condition,
                price: Number.parseFloat(price),
                currency: prereqs.marketplaceMeta?.currency || "USD",
                quantity: Number.parseInt(quantity, 10),
                categoryId,
                fulfillmentPolicyId,
                paymentPolicyId,
                returnPolicyId,
                merchantLocationKey,
                imageUrls,
                brand: p.brand || null,
                mpn: p.mpn || null,
                upc: p.productUpc || p.upc || null,
                ean: p.ean || null,
                weight: p.weight ? Number.parseFloat(p.weight) : null,
                weightUnit: (p.weightUnit || "POUND").toUpperCase(),
                dimensions: (p.length && p.width && p.height) ? {
                    length: Number.parseFloat(p.length),
                    width: Number.parseFloat(p.width),
                    height: Number.parseFloat(p.height),
                    unit: (p.dimensionUnit || "INCH").toUpperCase(),
                } : null,
                aspects: buildAspectsPayload(aspectValues, allAspects),
                existingListing: existingListing ? {
                    externalListingId: existingListing.externalListingId,
                    externalOfferId: existingListing.externalOfferId,
                    listingStatus: existingListing.listingStatus,
                    listingUrl: existingListing.listingUrl,
                } : null,
            };

            const resp = await publishMarketplaceListing(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                inventoryProductId: productId,
                marketplaceId,
                payload,
            });

            const body = resp?.data;
            if (body?.success !== true) {
                toast.error(body?.error?.message || "Failed to publish to eBay.");
                onPublishComplete?.({ success: false, lastError: body?.error?.message });
                return;
            }
            const result = body.data || {};
            if (result.success) {
                setPublishResult(result);
                toast.success(existingListing ? "Listing updated on eBay." : "Listing published to eBay.");
                onPublishComplete?.(result);
            } else {
                toast.error(result.lastError || "eBay rejected the listing.");
                onPublishComplete?.(result);
            }
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err.message || "Failed to publish to eBay.";
            toast.error(msg);
            onPublishComplete?.({ success: false, lastError: msg });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <output className="spinner-border text-primary" aria-hidden="true" />
                <p className="mt-3 text-muted">Fetching listing fields from eBay…</p>
            </div>
        );
    }

    if (missingLocations) {
        return (
            <div className="alert alert-warning">
                <strong>Create an eBay inventory location first.</strong>
                <p className="mb-2 mt-2">{missingLocations}</p>
                <p className="mb-2">
                    Visit{" "}
                    <a href={LOCATION_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub
                    </a>{" "}
                    to register at least one shipping location. Then come back and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }

    if (policiesOptedOut) {
        return (
            <div className="alert alert-warning">
                <strong>Opt in to eBay business policies first.</strong>
                <p className="mb-2 mt-2">{policiesOptedOut}</p>
                <p className="mb-2">
                    eBay&apos;s newer business-policies model isn&apos;t enabled on this account.
                    Without it, listings can&apos;t reference fulfillment / payment / return policies.
                </p>
                <p className="mb-2">
                    Visit{" "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub — Business Policies
                    </a>{" "}
                    and click <em>Opt in</em>. Then create at least one policy of each type, and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }

    if (missingPolicies) {
        return (
            <div className="alert alert-warning">
                <strong>Set up your eBay business policies first.</strong>
                <p className="mb-2 mt-2">{missingPolicies}</p>
                <p className="mb-2">
                    Visit{" "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub — Business Policies
                    </a>{" "}
                    to create a fulfillment, payment, and return policy. Then come back and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="alert alert-danger">
                {loadError}
                <div className="mt-2">
                    <Button className="cus-primary-transparent-btn" onClick={onSkip}>Skip eBay</Button>
                </div>
            </div>
        );
    }

    if (publishResult) {
        return (
            <div className="alert alert-success">
                <strong>
                    {existingListing ? "Listing updated on eBay!" : "Listing published to eBay!"}
                </strong>
                {publishResult.listingUrl && (
                    <p className="mb-0 mt-2">
                        <a href={publishResult.listingUrl} target="_blank" rel="noreferrer">
                            View on eBay →
                        </a>
                    </p>
                )}
            </div>
        );
    }

    if (!prereqs) return null;

    return (
        <>
            <p className="mb-3" style={{ fontSize: "13px", color: "#666" }}>
                <strong>{prereqs.product?.productSku || product.productSku}</strong>
                {` — ${prereqs.product?.productName || product.productName || ""}`}
                {existingListing && (
                    <span className="badge badge-info ml-2">Existing listing — Update mode</span>
                )}
            </p>

            <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Form.Group>

            <div className="row">
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Price ({prereqs.marketplaceMeta?.currency || "USD"}) *</Form.Label>
                    <Form.Control type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} />
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Quantity *</Form.Label>
                    <Form.Control type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Condition *</Form.Label>
                    <Form.Select value={condition} onChange={(e) => setCondition(e.target.value)}>
                        <option value="New">New</option>
                        <option value="New Other">New Other</option>
                        <option value="Like New">Like New</option>
                        <option value="Manufacturer Refurbished">Manufacturer Refurbished</option>
                        <option value="Seller Refurbished">Seller Refurbished</option>
                        <option value="Used Excellent">Used — Excellent</option>
                        <option value="Used Very Good">Used — Very Good</option>
                        <option value="Used Good">Used — Good</option>
                        <option value="Used Acceptable">Used — Acceptable</option>
                        <option value="For Parts or Not Working">For Parts or Not Working</option>
                    </Form.Select>
                </Form.Group>
            </div>

            <Form.Group className="mb-3">
                <Form.Label>Category *</Form.Label>
                <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    {prereqs.suggestedCategory && (
                        <option value={prereqs.suggestedCategory.categoryId}>
                            {prereqs.suggestedCategory.categoryPath || prereqs.suggestedCategory.categoryName}
                        </option>
                    )}
                    {(prereqs.otherCategorySuggestions || []).map((c) => (
                        <option key={c.categoryId} value={c.categoryId}>
                            {c.categoryPath || c.categoryName}
                        </option>
                    ))}
                </Form.Select>
                <Form.Text className="text-muted">
                    Required eBay fields below depend on this category. Changing the category may require different fields — re-open the modal to refresh.
                </Form.Text>
            </Form.Group>

            <div className="row">
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Fulfillment Policy *</Form.Label>
                    <Form.Select value={fulfillmentPolicyId} onChange={(e) => setFulfillmentPolicyId(e.target.value)}>
                        {(prereqs.policies?.fulfillment || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Payment Policy *</Form.Label>
                    <Form.Select value={paymentPolicyId} onChange={(e) => setPaymentPolicyId(e.target.value)}>
                        {(prereqs.policies?.payment || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Return Policy *</Form.Label>
                    <Form.Select value={returnPolicyId} onChange={(e) => setReturnPolicyId(e.target.value)}>
                        {(prereqs.policies?.return || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>

            <Form.Group className="mb-3">
                <Form.Label>Warehouse Location *</Form.Label>
                <Form.Select value={merchantLocationKey} onChange={(e) => setMerchantLocationKey(e.target.value)}>
                    {(prereqs.locations || []).map((l) => (
                        <option key={l.key} value={l.key}>{l.name}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            {prereqs.requiredFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        Required Fields ({prereqs.requiredFields.length})
                    </legend>
                    {prereqs.requiredFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>
                                {aspect.name} <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.recommendedFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowRecommended((v) => !v)}
                            aria-expanded={showRecommended}
                        >
                            <i className={`fa fa-chevron-${showRecommended ? "down" : "right"} mr-2`} />
                            Recommended Fields ({prereqs.recommendedFields.length})
                        </button>
                    </legend>
                    {showRecommended && prereqs.recommendedFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.optionalFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowOptional((v) => !v)}
                            aria-expanded={showOptional}
                        >
                            <i className={`fa fa-chevron-${showOptional ? "down" : "right"} mr-2`} />
                            Optional Fields ({prereqs.optionalFields.length})
                        </button>
                    </legend>
                    {showOptional && prereqs.optionalFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            <p style={{ fontSize: 12, color: "#888", marginTop: 16 }}>
                Publishing creates a billable listing on eBay. Insertion fees may apply depending on your store tier.
            </p>

            <div className="text-right">
                <Button
                    className="cus-seconday-bg-btn"
                    onClick={handlePublish}
                    disabled={submitting}
                >
                    {publishButtonLabel(submitting, !!existingListing)}
                </Button>
            </div>
        </>
    );
}

EbayStep.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productSku: PropTypes.string,
        productName: PropTypes.string,
        productDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        companySellerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    marketplaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onPublishComplete: PropTypes.func,
    onSkip: PropTypes.func,
};
EbayStep.defaultProps = { onPublishComplete: undefined, onSkip: undefined };
